// The client-side twin of `shared/src/money.ts`'s `allocate` — the proportional
// tax-spread rule the Point Click Care export applies once per invoice, across
// every GL-account group it touched, in `backend/src/domain/exportBuilder.ts`
// (see `docs/export.md` → Sales tax for the rule in prose).
//
// `app/src` deliberately does not depend on `@ap/shared` — the same reason
// `ESCALATION_DAYS`/`ESCALATION_REASON` are hand-copied in `lib/constants.ts`
// and `MASTER_PAGE_LIMIT` is hand-copied in `lib/data.ts` — so `AccountsSummary`'s
// "Distribute tax" toggle needs its OWN copy of the algorithm too, for the same
// reason `app/mock/allocate.ts` exists for the local harness's export preview:
// this package keeps hand-written twins of the wire vocabulary rather than
// importing the backend's package (`app/README.md`). THAT IS THE SAME HAZARD
// documented on `app/mock/allocate.ts`: two (here, three counting the mock's)
// independent copies of a byte-exact money rule can drift, and this one decides
// what a reviewer sees when told "this is the same figure the CSV will carry."
// `app/mock/tests/tax-distribution.test.ts` pins this against the real
// `allocate` (a relative-path, test-only import — the same technique
// `app/mock/tests/allocate-parity.test.ts` uses) and against a live export
// through the mock's own `createExport`, so the number this module hands back
// is proved equal to the number that lands in the CSV, not just structurally
// similar to it. If you change one, change the other in the same commit.
//
// UNITS: this package holds money as JS-number DOLLARS end to end
// (app/README.md's "Money is a string on the wire" — the client parses it to a
// `number`), never integer cents. But the ALLOCATION arithmetic itself — the
// floor-then-remainder split — runs in whole cents internally, exactly as
// `shared/src/money.ts`'s `allocate` does, and converts back to dollars only at
// the boundary. Never run this division in floating dollars: that is how a
// one-cent remainder gets lost or duplicated, which is exactly the historical
// defect `docs/export.md` → Sales tax records.

/**
 * Spread `amount` (dollars) across `bases` (dollars), proportional to each
 * base's magnitude, in exact cents. Byte-for-byte the same rule as
 * `shared/src/money.ts`'s `allocate` (and `app/mock/allocate.ts`'s twin of it):
 *
 *  1. Every base's sign is dropped for the ratio. If every base is zero (or
 *     there are none), there is nothing to spread proportionally to: every
 *     share is `0` — the one case where the shares do not sum to `amount`.
 *  2. Each base's raw share is `floor(|amount| * |base_i| / total)`, in cents.
 *  3. ALL leftover cents go to the group with the single LARGEST unsigned
 *     base; a tie goes to the LOWER of the two `sequences` (not array order).
 *  4. Every share takes `amount`'s sign; a zero share stays `0`, never `-0`.
 *
 * `bases` and `sequences` must be the same length and line up index for index.
 */
export function allocateTaxDollars(
  amount: number,
  bases: readonly number[],
  sequences: readonly number[],
): number[] {
  const n = bases.length
  if (n !== sequences.length) {
    throw new RangeError('allocateTaxDollars: bases and sequences must be the same length')
  }
  if (n === 0) return []

  const sign = amount < 0 ? -1 : 1
  const amountCents = Math.round(Math.abs(amount) * 100)
  const baseCents = bases.map((b) => Math.round(Math.abs(b) * 100))
  const totalCents = baseCents.reduce((a, b) => a + b, 0)
  if (totalCents === 0) return baseCents.map(() => 0)

  const shareCents = baseCents.map((b) => Math.floor((amountCents * b) / totalCents))
  const leftover = amountCents - shareCents.reduce((a, b) => a + b, 0)

  let winner = 0
  for (let i = 1; i < n; i++) {
    const largerBase = (baseCents[i] as number) > (baseCents[winner] as number)
    const tiedLowerSequence =
      baseCents[i] === baseCents[winner] && (sequences[i] as number) < (sequences[winner] as number)
    if (largerBase || tiedLowerSequence) winner = i
  }
  shareCents[winner] = (shareCents[winner] as number) + leftover

  // `0 * -1` is `-0` in JavaScript; keep a zero share as `0`, matching the real
  // `allocate` and its mock twin.
  return shareCents.map((cents) => (cents === 0 ? 0 : (cents * sign) / 100))
}

/**
 * One grouped row this module can distribute tax across: a total in dollars,
 * plus the earliest line-item sequence rolled into the group — the same
 * tie-break key `exportBuilder.ts` uses (the earliest `line_number` folded into
 * that GL-account group).
 *
 * KNOWN GAP: `AccountsSummary`'s own caller, `InvoiceDetailModal.tsx`, does not
 * thread `line_number` into the rows it hands `AccountsSummary` today, so a row
 * built from that path always ties at `0`. That only matters when two account
 * groups land on the EXACT same pre-tax subtotal and the rounding remainder has
 * to land on one of them — the total shown is unaffected either way, and the
 * remainder still lands somewhere; it can just be a different tied group than
 * the export would have picked, by at most one cent. Recorded in
 * `docs/questions.md` rather than fixed here, since threading it through is an
 * edit to a file outside this change's scope.
 */
export interface DistributableRow {
  total: number
  sequence?: number
}

/**
 * Distribute `taxDollars` across grouped rows, returning a NEW array — same
 * length and order as `rows`, never mutating it — with each row's `total`
 * replaced by its tax-inclusive figure: the same figure the Point Click Care
 * export will carry for that GL account, PROVIDED `rows` is the invoice's
 * FULL pool of account groups — every company it touches, not one company's
 * subset (`docs/export.md` → Sales tax: spreading within a subset would spread
 * the whole tax again on a split invoice and over-collect by exactly the tax).
 */
export function distributeTax<T extends DistributableRow>(rows: readonly T[], taxDollars: number): T[] {
  if (rows.length === 0) return []
  const bases = rows.map((r) => r.total)
  const sequences = rows.map((r) => r.sequence ?? 0)
  const shares = allocateTaxDollars(taxDollars, bases, sequences)
  return rows.map((r, i) => ({ ...r, total: roundToCents(r.total + (shares[i] ?? 0)) }))
}

/** Dollars, rounded to the cent — kills the float noise from adding a
    cents-derived share to a summed dollar total (e.g. `37 + 2.71` can land on
    `39.709999999999994`), the same guard `app/mock/exportCsv.ts` applies after
    its own `allocate` call. */
function roundToCents(n: number): number {
  return Math.round(n * 100) / 100
}
